/* eslint-disable import/prefer-default-export, no-underscore-dangle, no-console */
import React from 'react';
import ReactDOM from 'react-dom';
import templates from '../templates';
import { importData, replaceText, translationsForPdf } from '../utils';
import AppContext from '../context/AppContext';
import Hays from '../templates/hays/Hays';
import {
  MONTHS_YEARS_FORMAT,
  YEARS_ONLY_FORMAT,
  EXCLUDE_DATES_FORMAT,
} from '../constants/exportDateFormatTypes';

export const PDF_FORMAT = 'PDF';
export const GOOGLE_DOC_FORMAT = 'Google Doc';

export const PROJECT_DATES_FORMAT_OPTIONS = [
  MONTHS_YEARS_FORMAT,
  YEARS_ONLY_FORMAT,
  EXCLUDE_DATES_FORMAT,
];
export const FILE_FORMAT_OPTIONS = [GOOGLE_DOC_FORMAT, PDF_FORMAT];

export const TEMPLATE_FILE_FORMATS = {
  softteco: [PDF_FORMAT, GOOGLE_DOC_FORMAT],
  hays: [PDF_FORMAT],
  'kanda software': [GOOGLE_DOC_FORMAT],
  sarab: [GOOGLE_DOC_FORMAT],
};

export const getAvailableFileFormats = (templateName) => {
  if (!templateName || typeof templateName !== 'string') {
    return FILE_FORMAT_OPTIONS;
  }
  const normalized = templateName.toLowerCase().trim();
  const key = Object.keys(TEMPLATE_FILE_FORMATS).find((k) => normalized.includes(k));
  return key ? TEMPLATE_FILE_FORMATS[key] : FILE_FORMAT_OPTIONS;
};

export const isFileFormatEnabled = (templateName, fileFormat) =>
  getAvailableFileFormats(templateName).includes(fileFormat);

export const getTemplateUrlByName = (companyTemplates, templateName) => {
  const template = companyTemplates.find((t) => t.name === templateName);
  return template ? template.url : null;
};

const createModalPdf = (text) => {
  const messageElement = document.createElement('div');
  messageElement.innerText = text;
  Object.assign(messageElement.style, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    padding: '20px',
    borderRadius: '8px',
    zIndex: 1000,
    textAlign: 'center',
  });

  return messageElement;
};

const ensureActiveResumeLoaded = (context) => {
  const {
    state: { resumeItems, selectedCvId, ownCvId },
    dispatch,
  } = context;

  const activeId = selectedCvId || ownCvId;
  if (!activeId || !resumeItems || resumeItems.length === 0) {
    return;
  }
  const activeResume =
    resumeItems.find((item) => item.id === activeId || item._id === activeId) || resumeItems[0];
  if (activeResume) {
    importData(dispatch, { data: { ...activeResume } });
  }
};

const getActiveResume = (context) => {
  const {
    state: { selectedCvId, ownCvId, resumeItems },
  } = context;
  const activeId = selectedCvId || ownCvId;
  if (!resumeItems?.length) return null;

  return (
    resumeItems.find((item) => item.id === activeId || item._id === activeId) || resumeItems[0]
  );
};

const waitForRender = (ms = 500) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export const handlePdf = async (spinner, context, handleClose) => {
  const {
    state: { theme },
  } = context;
  ensureActiveResumeLoaded(context);

  const messageElement = createModalPdf('Please save document as PDF');
  document.body.appendChild(messageElement);

  await waitForRender();
  spinner.start();

  const printPage = document.getElementById('printPage');
  const originalHTML = printPage.innerHTML;

  const currentTemplate = templates.find((x) => theme?.layout?.toLowerCase() === x.key);
  const TemplateComponent = currentTemplate?.component;
  ReactDOM.render(
    <AppContext.Provider value={context}>
      {TemplateComponent ? <TemplateComponent /> : null}
    </AppContext.Provider>,
    printPage,
  );

  printPage.querySelectorAll('.hide-on-print').forEach((el) => {
    el.style.display = 'none';
  });

  await new Promise((resolve) => setTimeout(resolve, 1000));

  window.print();

  document.body.removeChild(messageElement);

  ReactDOM.unmountComponentAtNode(printPage);
  printPage.innerHTML = originalHTML;

  spinner.stop();
  handleClose();
};

export const handleRusPdf = async (context, spinner, handleClose) => {
  const {
    state: { theme },
  } = context;
  ensureActiveResumeLoaded(context);

  const messageElement = createModalPdf('Пожалуйста, сохраните документ как PDF');
  document.body.appendChild(messageElement);

  const activeResume = getActiveResume(context);

  const printContext = {
    ...context,
    state: {
      ...context.state,
      resume: activeResume,
    },
  };

  await waitForRender();
  spinner.start();

  const printPage = document.getElementById('printPage');
  const originalHTML = printPage.innerHTML;

  const currentTemplate = templates.find((x) => theme?.layout?.toLowerCase() === x.key);
  const TemplateComponent = currentTemplate?.component;
  ReactDOM.render(
    <AppContext.Provider value={printContext}>
      {TemplateComponent ? <TemplateComponent /> : null}
    </AppContext.Provider>,
    printPage,
  );

  let htmlContent = printPage.innerHTML;
  htmlContent = replaceText(htmlContent, translationsForPdf);
  printPage.innerHTML = htmlContent;

  printPage.querySelectorAll('.hide-on-print').forEach((el) => {
    el.style.display = 'none';
  });

  await new Promise((resolve) => setTimeout(resolve, 1000));

  spinner.stop();
  handleClose();
  setTimeout(() => {
    window.print();

    document.body.removeChild(messageElement);

    ReactDOM.unmountComponentAtNode(printPage);
    printPage.innerHTML = originalHTML;
  }, 0);
};

const injectHaysPrintStyles = () => {
  const css = `
      @page { size: A4; margin: 30px 0 22px 0; }
      @media print {
        body::before {
          content: '';
          position: fixed;
          top: 0; left: 26px;
          width: calc(100% - 52px);
          height: 2px;
          background-color: #c63031;
          z-index: 9999;
          visibility: visible;
        }
      }
    `;
  const style = document.createElement('style');
  style.id = 'hays-print-overrides';
  style.innerHTML = css;
  document.head.appendChild(style);
  return style;
};

const removeHaysPrintStyles = (styleEl) => {
  if (styleEl && styleEl.parentNode) {
    styleEl.parentNode.removeChild(styleEl);
  }
};

export const handleHaysPdf = async (context, spinner, handleClose) => {
  // ensureActiveResumeLoaded(context);
  spinner.start();
  const messageElement = createModalPdf('Please save HAYS PDF');
  document.body.appendChild(messageElement);

  const printPage = document.getElementById('printPage');
  const originalHTML = printPage.innerHTML;

  ReactDOM.render(
    <AppContext.Provider value={context}>
      <Hays />
    </AppContext.Provider>,
    printPage,
  );

  document.querySelectorAll('.hide-on-print').forEach((el) => {
    el.style.display = 'none';
  });

  const styleEl = injectHaysPrintStyles();

  await new Promise((r) => setTimeout(r, 1000));

  window.print();
  document.body.removeChild(messageElement);

  removeHaysPrintStyles(styleEl);
  ReactDOM.unmountComponentAtNode(printPage);
  printPage.innerHTML = originalHTML;
  spinner.stop();
  handleClose();
};

export const handleExportPdf = async (chosenTemplate, context, spinner, handleClose) => {
  if (chosenTemplate === 'Hays') {
    return handleHaysPdf(context, spinner, handleClose);
  }
  if (chosenTemplate === 'SoftTeco RU') {
    return handleRusPdf(context, spinner, handleClose);
  }
  return handlePdf(spinner, context, handleClose);
};
